import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Boxes, ArrowRight, AlertCircle } from 'lucide-react';
import { registerThunk, selectAuthLoading, selectAuthError, selectIsLoggedIn, clearError } from '../store/authSlice';
import { INDIAN_STATES } from '../lib/format';

export default function Register() {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const isLoading  = useSelector(selectAuthLoading);
  const error      = useSelector(selectAuthError);
  const isLoggedIn = useSelector(selectIsLoggedIn);

  const [form, setForm] = useState({
    businessName: '', ownerName: '', email: '', password: '', state: 'Odisha', stateCode: 'OD',
  });

  const STATE_CODES = {
    'Odisha': 'OD', 'Maharashtra': 'MH', 'Karnataka': 'KA', 'West Bengal': 'WB',
    'Tamil Nadu': 'TN', 'Delhi': 'DL', 'Rajasthan': 'RJ', 'Telangana': 'TS',
    'Andhra Pradesh': 'AP', 'Gujarat': 'GJ', 'Uttar Pradesh': 'UP', 'Bihar': 'BR',
  };

  useEffect(() => { if (isLoggedIn) navigate('/dashboard', { replace: true }); }, [isLoggedIn, navigate]);
  useEffect(() => { return () => dispatch(clearError()); }, [dispatch]);

  const update = (k, v) => {
    const updates = { [k]: v };
    if (k === 'state') updates.stateCode = STATE_CODES[v] || '';
    setForm((f) => ({ ...f, ...updates }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(registerThunk(form));
    if (registerThunk.fulfilled.match(result)) navigate('/dashboard', { replace: true });
  };

  const input = (label, key, type = 'text', placeholder = '') => (
    <div className="space-y-1.5">
      <label className="text-xs uppercase tracking-wider text-slate-600">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => update(key, e.target.value)}
        placeholder={placeholder}
        required
        className="flex h-11 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />
    </div>
  );

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#1E3A5F]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F] via-[#1E3A5F]/90 to-[#2E6DA4]/50" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-md bg-white/10 ring-1 ring-white/20 flex items-center justify-center">
              <Boxes className="w-5 h-5" />
            </div>
            <div>
              <div className="text-lg font-semibold font-heading">SlipSense</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-300">Payroll OS</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-300 mb-3">Start in under 2 minutes</div>
            <h1 className="text-4xl xl:text-5xl font-semibold tracking-tight font-heading leading-tight">
              Modern payroll for<br />small businesses.
            </h1>
            <p className="mt-4 text-slate-300 text-base max-w-md">
              No credit card required. Add employees, set salaries, run payroll — all from one workspace.
            </p>
          </div>
          <div className="text-xs text-slate-400">© 2026 SlipSense</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 overflow-y-auto">
        <div className="w-full max-w-sm py-6">
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Get started</div>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 font-heading">Create your account</h2>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 mb-4">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {input('Business Name', 'businessName', 'text', 'Sharma Enterprises')}
            {input('Owner Name', 'ownerName', 'text', 'Rahul Sharma')}
            {input('Email', 'email', 'email', 'you@business.in')}
            {input('Password (min 8 chars)', 'password', 'password', '••••••••')}

            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-slate-600">State</label>
              <select
                value={form.state}
                onChange={(e) => update('state', e.target.value)}
                className="flex h-11 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Plan selection */}
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wider text-slate-600">Plan</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'starter', label: 'Starter', price: '₹499/mo', desc: 'Up to 8 employees' },
                  { id: 'pro',     label: 'Pro',     price: '₹1,299/mo', desc: 'Unlimited employees' },
                ].map((p) => (
                  <div key={p.id} className={`border rounded-md p-3 cursor-pointer transition-colors ${form.plan === p.id ? 'border-[#2E6DA4] bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                    onClick={() => update('plan', p.id)}>
                    <div className="text-sm font-medium text-slate-900">{p.label}</div>
                    <div className="text-xs text-[#2E6DA4] font-medium">{p.price}</div>
                    <div className="text-xs text-slate-500">{p.desc}</div>
                  </div>
                ))}
              </div>
              <Link to="/pricing" className="text-xs text-[#2E6DA4] hover:underline block text-right">Compare plans →</Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 bg-[#2E6DA4] hover:bg-[#255a88] disabled:opacity-60 text-white font-medium rounded-md flex items-center justify-center gap-2 transition-colors"
            >
              {isLoading ? 'Creating…' : <>Create Account <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-6 text-sm text-slate-500 text-center">
            Already have an account?{' '}
            <Link to="/login" className="text-[#2E6DA4] font-medium hover:underline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}